import React, { createContext, useContext, useState } from 'react';
import axios from 'axios';

const ApiContext = createContext();

export const useApi = () => {
  const context = useContext(ApiContext);
  if (!context) {
    throw new Error('useApi must be used within an ApiProvider');
  }
  return context;
};

export const ApiProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);

  // Utility functions
  const handleApiError = (error) => {
    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response;

      // Note: 401 handling is managed by AuthContext's axios interceptor
      // to ensure consistent token refresh behavior

      return {
        success: false,
        message: data.error || data.message || `Server error (${status})`,
        status
      };
    } else if (error.request) {
      // Network error
      return {
        success: false,
        message: 'Network error. Please check your connection.',
        status: 0
      };
    } else {
      // Other error
      return {
        success: false,
        message: error.message || 'An unexpected error occurred',
        status: -1
      };
    }
  };

  const makeApiCall = async (apiCall) => {
    try {
      const response = await apiCall();
      return {
        success: true,
        data: response.data,
        status: response.status
      };
    } catch (error) {
      return handleApiError(error);
    }
  };

  // Direct API call function that the Events component expects
  const apiCall = async (url, options = {}) => {
    setLoading(true);
    try {
      const response = await axios(url, options);
      return response.data;
    } catch (error) {
      console.error('API call failed:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Drivers API
  const drivers = {
    getAll: (params = {}) => makeApiCall(() => axios.get('/api/drivers', { params })),
    getById: (id) => makeApiCall(() => axios.get(`/api/drivers/${id}`)),
    getRaceHistory: (id, params = {}) => makeApiCall(() => axios.get(`/api/drivers/${id}/races`, { params })),
    getStatistics: (id) => makeApiCall(() => axios.get(`/api/drivers/${id}/statistics`)),
    getDropdownData: (type) => makeApiCall(() => axios.get(`/api/drivers/data/${type}`)),
    checkDuplicate: (firstName, lastName, excludeId) => makeApiCall(() =>
      axios.get('/api/drivers/check-duplicate', {
        params: { first_name: firstName, last_name: lastName, exclude_id: excludeId }
      })
    ),
    create: (data) => makeApiCall(() => axios.post('/api/drivers', data)),
    update: (id, data) => makeApiCall(() => axios.put(`/api/drivers/${id}`, data)),
    delete: (id) => makeApiCall(() => axios.delete(`/api/drivers/${id}`))
  };

  // Events API
  const events = {
    getAll: (params = {}) => makeApiCall(() => axios.get('/api/events', { params })),
    getById: (id) => makeApiCall(() => axios.get(`/api/events/${id}`)),
    getCalendarData: (params = {}) => makeApiCall(() => axios.get('/api/events/calendar', { params })),
    getCopyableEvents: (id) => makeApiCall(() => axios.get(`/api/events/${id}/copyable-events`)),
    getSourceEntries: (id, sourceEventId) => makeApiCall(() =>
      axios.get(`/api/events/${id}/copyable-events/${sourceEventId}/entries`)
    ),
    importEntriesFrom: (id, sourceEventId, driverIds) => makeApiCall(() =>
      axios.post(`/api/events/${id}/entries/import-from/${sourceEventId}`,
        Array.isArray(driverIds) ? { driver_ids: driverIds } : {})
    ),
    getQualifyingPrintURL: (id, raceEventId) => {
      const baseURL = axios.defaults.baseURL || '';
      const qs = raceEventId ? `?race_event_id=${raceEventId}` : '';
      return `${baseURL}/api/events/${id}/qualifying-print${qs}`;
    },
    getRaces: (id) => makeApiCall(() => axios.get(`/api/events/${id}/races`)),
    create: (data) => makeApiCall(() => axios.post('/api/events', data)),
    update: (id, data) => makeApiCall(() => axios.put(`/api/events/${id}`, data)),
    delete: (id) => makeApiCall(() => axios.delete(`/api/events/${id}`))
  };

  // Tracks API
  const tracks = {
    getAll: (params = {}) => makeApiCall(() => axios.get('/api/tracks', { params })),
    getById: (id) => makeApiCall(() => axios.get(`/api/tracks/${id}`)),
    getUpcomingEvents: (id, params = {}) => makeApiCall(() => axios.get(`/api/tracks/${id}/events`, { params })),
    getRecords: (id) => makeApiCall(() => axios.get(`/api/tracks/${id}/records`)),
    getStatistics: (id) => makeApiCall(() => axios.get(`/api/tracks/${id}/statistics`)),
    getStates: () => makeApiCall(() => axios.get('/api/tracks/data/states')),
    getTrackTypes: () => makeApiCall(() => axios.get('/api/tracks/data/types')),
    getSeries: () => makeApiCall(() => axios.get('/api/series')),
    create: (data) => makeApiCall(() => axios.post('/api/tracks', data)),
    update: (id, data) => makeApiCall(() => axios.put(`/api/tracks/${id}`, data)),
    delete: (id) => makeApiCall(() => axios.delete(`/api/tracks/${id}`))
  };

  // Races API
  const races = {
    getAll: (params = {}) => makeApiCall(() => axios.get('/api/races', { params })),
    getById: (id) => makeApiCall(() => axios.get(`/api/races/${id}`)),
    getResults: (id) => makeApiCall(() => axios.get(`/api/races/${id}/results`)),
    getLineup: (id) => makeApiCall(() => axios.get(`/api/races/${id}/lineup`)),
    getAvailableDrivers: (id) => makeApiCall(() => axios.get(`/api/races/${id}/available-drivers`)),
    addToLineup: (id, entrantId) => makeApiCall(() => axios.post(`/api/races/${id}/lineup`, { entrant_id: entrantId })),
    bulkAddToLineup: (id, entrantIds) => makeApiCall(() => axios.post(`/api/races/${id}/lineup/bulk`, { entrant_ids: entrantIds })),
    removeFromLineup: (id, competitorId) => makeApiCall(() => axios.delete(`/api/races/${id}/lineup/${competitorId}`)),
    updateLineupPositions: (id, lineup) => makeApiCall(() => axios.put(`/api/races/${id}/lineup/positions`, { lineup })),
    updateTransponder: (raceId, competitorId, transponder) => makeApiCall(() => axios.put(`/api/races/${raceId}/lineup/${competitorId}/transponder`, { transponder })),
    getLineupExportURL: (id, type) => {
      const baseURL = axios.defaults.baseURL || '';
      return `${baseURL}/api/races/${id}/lineup/export/${type}`;
    },
    sendLineupEmail: (id, to, subject) => makeApiCall(() => axios.post(`/api/races/${id}/lineup/send-email`, { to, subject })),
    sendLineupToLTS: (id) => makeApiCall(() => axios.post(`/api/races/${id}/lineup/send-lts`)),
    getEventSeries: (eventId) => makeApiCall(() => axios.get(`/api/races/event/${eventId}/series`)),
    getRaceTypes: () => makeApiCall(() => axios.get('/api/events/data/race-types')),
    create: (data) => makeApiCall(() => axios.post('/api/races', data)),
    update: (id, data) => makeApiCall(() => axios.put(`/api/races/${id}`, data)),
    delete: (id) => makeApiCall(() => axios.delete(`/api/races/${id}`))
  };

  // Series API
  const series = {
    getAll: (params = {}) => makeApiCall(() => axios.get('/api/series', { params })),
    getById: (id) => makeApiCall(() => axios.get(`/api/series/${id}`)),
    getSeasonDashboard: (id) => makeApiCall(() => axios.get(`/api/series/${id}/season-dashboard`)),
    getSeasons: (id) => makeApiCall(() => axios.get(`/api/series/${id}/seasons`)),
    createSeason: (id, data) => makeApiCall(() => axios.post(`/api/series/${id}/seasons`, data)),
    updateSeason: (id, seasonId, data) => makeApiCall(() => axios.put(`/api/series/${id}/seasons/${seasonId}`, data)),
    getPreloadStats: (id) => makeApiCall(() => axios.get(`/api/series/${id}/preload-stats`)),
    getClasses: (id) => makeApiCall(() => axios.get(`/api/series/${id}/classes`)),
    getAnalytics: () => makeApiCall(() => axios.get('/api/series/analytics/overview')),
    getTypes: () => makeApiCall(() => axios.get('/api/series/data/types')),
    getDivisions: () => makeApiCall(() => axios.get('/api/series/data/divisions')),
    create: (data) => makeApiCall(() => axios.post('/api/series', data)),
    update: (id, data) => makeApiCall(() => axios.put(`/api/series/${id}`, data)),
    delete: (id) => makeApiCall(() => axios.delete(`/api/series/${id}`)),
    getResults: (id, params = {}) => makeApiCall(() => axios.get(`/api/series/${id}/results`, { params })),
    getResultsHealth: (id, params = {}) => makeApiCall(() => axios.get(`/api/series/${id}/results-health`, { params }))
  };

  // Results API
  const results = {
    getAll: (params = {}) => makeApiCall(() => axios.get('/api/results', { params })),
    getByRace: (raceId, params = {}) => makeApiCall(() => axios.get(`/api/results/race/${raceId}`, { params })),
    getByDriver: (driverId, params = {}) => makeApiCall(() => axios.get(`/api/results/driver/${driverId}`, { params })),
    getByTrack: (trackId, params = {}) => makeApiCall(() => axios.get(`/api/results/track/${trackId}`, { params })),
    create: (data) => makeApiCall(() => axios.post('/api/results', data)),
    update: (id, data) => makeApiCall(() => axios.put(`/api/results/${id}`, data)),
    updateRace: (raceId, data) => makeApiCall(() => axios.put(`/api/results/race/${raceId}`, data)),
    delete: (id) => makeApiCall(() => axios.delete(`/api/results/${id}`))
  };

  // Promotions API
  const promotions = {
    getAll: (params = {}) => makeApiCall(() => axios.get('/api/promotions', { params })),
    getById: (id) => makeApiCall(() => axios.get(`/api/promotions/${id}`)),
    create: (data) => makeApiCall(() => axios.post('/api/promotions', data)),
    update: (id, data) => makeApiCall(() => axios.put(`/api/promotions/${id}`, data)),
    delete: (id) => makeApiCall(() => axios.delete(`/api/promotions/${id}`))
  };

  // Admin API
  const admin = {
    getStatistics: () => makeApiCall(() => axios.get('/api/admin/statistics')),
    getDashboard: () => makeApiCall(() => axios.get('/api/admin/dashboard')),
    getSystemHealth: () => makeApiCall(() => axios.get('/api/admin/health')),
    getUsers: (params = {}) => makeApiCall(() => axios.get('/api/admin/users', { params })),
    updateUser: (id, data) => makeApiCall(() => axios.put(`/api/admin/users/${id}`, data)),
    deleteUser: (id) => makeApiCall(() => axios.delete(`/api/admin/users/${id}`))
  };

  // LTS (Live Timing System) API
  const lts = {
    getStatus: (tpeId) => makeApiCall(() => axios.get(`/api/lts/status/${tpeId}`)),
    getCompetitors: (tpeId) => makeApiCall(() => axios.get(`/api/lts/competitors/${tpeId}`)),
    subscribe: (tpeId) => makeApiCall(() => axios.post(`/api/lts/subscribe/${tpeId}`)),
    unsubscribe: (tpeId) => makeApiCall(() => axios.delete(`/api/lts/subscribe/${tpeId}`)),
    getActiveRaces: () => makeApiCall(() => axios.get('/api/lts/active')),
    getConnectionStatus: () => makeApiCall(() => axios.get('/api/lts/connection')),
    getRaceData: (tpeId) => makeApiCall(() => axios.get(`/api/lts/race/${tpeId}`)),
    refresh: (tpeId) => makeApiCall(() => axios.post(`/api/lts/refresh/${tpeId}`))
  };

  // Home/Dashboard API
  const home = {
    getDashboard: () => makeApiCall(() => axios.get('/api/home/dashboard'))
  };

  const value = {
    drivers,
    events,
    tracks,
    races,
    series,
    results,
    promotions,
    admin,
    lts,
    home,
    makeApiCall,
    handleApiError,
    apiCall,
    loading
  };

  return (
    <ApiContext.Provider value={value}>
      {children}
    </ApiContext.Provider>
  );
}; 